import React, { useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';

import { usePokCore } from '../../../common/hooks/usePokCore';
import { useNotifications } from '../../../common/hooks/useNotifications';
import { useTranslation } from '../../../translations';

import { Option as BaseOption, Selector } from './Selector';

type Option = BaseOption;

export interface ProfileAllSelectorProps {
  onChange?: (option: Option) => void;
  projectId?: string;
  value?: string | undefined;
  showAllOption?: boolean;
}

export default (props: ProfileAllSelectorProps) => {
  const [profiles, setProfiles] = useState<Option[]>([]);
  const { t, tk } = useTranslation('projectLng');
  const pok = usePokCore();
  const notifications = useNotifications();

  useEffect(() => {
    let mounted = true;
    if (props.projectId) {
      pok.profiles
        .getAllForProject(props.projectId)
        .then(data => {
          if (mounted) {
            let options: Option[] = [];
            if (props.showAllOption && data.length > 1) {
              options.push({ value: 'all', label: t(tk.timeLine.all) });
            }
            options = options.concat(
              data.map(p => ({ value: p.id, label: p.name })),
            );

            setProfiles(options);
          }
        })
        .catch(errorResponse => {
          notifications.caughtError(errorResponse);
        });
    }
    return () => {
      mounted = false;
    };
  }, [
    props.projectId,
    pok.profiles,
    notifications,
    t,
    tk.timeLine.all,
    props.showAllOption,
  ]);

  const provider = async (text: string) => {
    profiles.sort((a, b) => {
      if (a.value === 'all') {
        return -1;
      }
      if (b.value === 'all') {
        return 1;
      }
      return a.label.localeCompare(b.label);
    });
    return profiles.filter(p =>
      p.label.toLowerCase().includes(text.toLowerCase()),
    );
  };

  if (!profiles) {
    return <Skeleton />;
  }

  return (
    <Selector
      uniqueKey={'profileAll-' + JSON.stringify(profiles)}
      creatable={false}
      provider={provider}
      onChange={option => {
        if (props.onChange && option && !Array.isArray(option)) {
          // Convert 'all' back to undefined for external consumers
          const singleOption = option as BaseOption;
          const value =
            singleOption.value === 'all' ? undefined : singleOption.value;
          props.onChange({ ...singleOption, value } as Option);
        }
      }}
      value={props.value ?? 'all'}
    />
  );
};
